import React, { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { StatusBadge } from "@/components/common/StatusBadge";
import { EmptyState } from "@/components/common/EmptyState";
import { toast } from "sonner";
import { MapPin, Plus, Pencil, Trash2 } from "lucide-react";

export default function Territories() {
  const { isAdmin } = useAuth();
  const [items, setItems] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, o, u] = await Promise.all([
        api.get("/territories"),
        api.get("/organizations"),
        api.get("/users").catch(() => ({ data: { users: [] } })),
      ]);
      setItems(t.data.items || []);
      setOrgs(o.data.organizations || []);
      setUsers(u.data.users || u.data.items || []);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    if (!window.confirm("Delete this recruiting territory?")) return;
    try { await api.delete(`/territories/${id}`); toast.success("Territory deleted"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">Recruiting Territories</h1>
          <p className="text-sm text-slate-500 mt-0.5">Configure ZIP-based recruiting areas per District for prospect area review.</p>
        </div>
        {isAdmin && <Button onClick={() => setEdit({})} data-testid="territory-new" className="bg-[#006090] hover:bg-[#004E75]"><Plus className="w-4 h-4 mr-1" /> New Territory</Button>}
      </div>

      {loading ? <p className="text-sm text-slate-400 mt-8">Loading…</p> : items.length === 0 ? (
        <div className="mt-10"><EmptyState icon={MapPin} title="No territories" description="Add a recruiting territory to enable area review." /></div>
      ) : (
        <div className="mt-6 grid md:grid-cols-2 gap-5">
          {items.map((t) => (
            <div key={t.id} className="rounded-xl border border-slate-200 bg-white p-5" data-testid={`territory-${t.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-semibold text-slate-900">{t.name}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{t.organization_name} · {t.state || "—"}</p>
                </div>
                <StatusBadge status={t.active ? "active" : "inactive"} label={t.active ? "Active" : "Inactive"} />
              </div>
              <p className="mt-3 text-xs text-slate-500">Public location: <span className="text-slate-700">{t.public_location || "—"}</span></p>
              <p className="mt-1 text-xs text-slate-500">ZIP prefixes: <span className="text-slate-700 font-mono">{(t.zip_prefixes || []).join(", ") || "—"}</span></p>
              <p className="mt-1 text-xs text-slate-500">Exact ZIPs: <span className="text-slate-700 font-mono">{(t.zip_codes || []).join(", ") || "—"}</span></p>
              {isAdmin && (
                <div className="mt-4 flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => setEdit(t)} data-testid={`territory-edit-${t.id}`}><Pencil className="w-3.5 h-3.5 mr-1" /> Edit</Button>
                  <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => del(t.id)} data-testid={`territory-delete-${t.id}`}><Trash2 className="w-3.5 h-3.5 mr-1" /> Delete</Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {edit && <TerritoryDialog territory={edit} orgs={orgs} users={users} onClose={() => setEdit(null)} onDone={() => { setEdit(null); load(); }} />}
    </div>
  );
}

function TerritoryDialog({ territory, orgs, users, onClose, onDone }) {
  const isNew = !territory.id;
  const [f, setF] = useState({
    name: territory.name || "",
    organization_id: territory.organization_id || "",
    state: territory.state || "",
    public_location: territory.public_location || "",
    zip_prefixes: (territory.zip_prefixes || []).join(", "),
    zip_codes: (territory.zip_codes || []).join(", "),
    default_recruiter_user_id: territory.default_recruiter_user_id || "",
    active: territory.active !== false,
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const csv = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);

  const submit = async () => {
    if (!f.name || !f.organization_id) return toast.error("Name and District are required.");
    setBusy(true);
    const payload = {
      name: f.name, organization_id: f.organization_id, state: f.state || null,
      public_location: f.public_location || null,
      zip_prefixes: csv(f.zip_prefixes), zip_codes: csv(f.zip_codes),
      default_recruiter_user_id: f.default_recruiter_user_id || null, active: f.active,
    };
    try {
      if (isNew) await api.post("/territories", payload);
      else await api.patch(`/territories/${territory.id}`, payload);
      toast.success(isNew ? "Territory created" : "Territory updated");
      onDone();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-lg" data-testid="territory-dialog">
        <DialogHeader>
          <DialogTitle>{isNew ? "New Recruiting Territory" : "Edit Recruiting Territory"}</DialogTitle>
          <DialogDescription>Match prospects to a District by ZIP prefix, exact ZIP, or state.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div><Label className="text-xs font-semibold text-slate-600">Name</Label>
            <Input className="mt-1.5" value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="territory-name" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs font-semibold text-slate-600">District</Label>
              <Select value={f.organization_id} onValueChange={(v) => set("organization_id", v)}>
                <SelectTrigger className="mt-1.5" data-testid="territory-org"><SelectValue placeholder="District" /></SelectTrigger>
                <SelectContent className="bg-white max-h-72">{orgs.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label className="text-xs font-semibold text-slate-600">State</Label>
              <Input className="mt-1.5" maxLength={2} value={f.state} onChange={(e) => set("state", e.target.value.toUpperCase())} placeholder="TX" data-testid="territory-state" /></div>
          </div>
          <div><Label className="text-xs font-semibold text-slate-600">Public Location Label</Label>
            <Input className="mt-1.5" value={f.public_location} onChange={(e) => set("public_location", e.target.value)} placeholder="Houston, Texas" data-testid="territory-location" /></div>
          <div><Label className="text-xs font-semibold text-slate-600">ZIP Prefixes (comma-separated)</Label>
            <Input className="mt-1.5" value={f.zip_prefixes} onChange={(e) => set("zip_prefixes", e.target.value)} placeholder="770, 771, 772" data-testid="territory-prefixes" /></div>
          <div><Label className="text-xs font-semibold text-slate-600">Exact ZIPs (optional, comma-separated)</Label>
            <Input className="mt-1.5" value={f.zip_codes} onChange={(e) => set("zip_codes", e.target.value)} placeholder="77002, 77003" data-testid="territory-zips" /></div>
          <div><Label className="text-xs font-semibold text-slate-600">Default Recruiter</Label>
            <Select value={f.default_recruiter_user_id || "none"} onValueChange={(v) => set("default_recruiter_user_id", v === "none" ? "" : v)}>
              <SelectTrigger className="mt-1.5" data-testid="territory-recruiter"><SelectValue placeholder="Select recruiter" /></SelectTrigger>
              <SelectContent className="bg-white max-h-72">
                <SelectItem value="none">None</SelectItem>
                {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} · {u.email}</SelectItem>)}
              </SelectContent>
            </Select></div>
          <div className="flex items-center gap-3"><Switch checked={f.active} onCheckedChange={(v) => set("active", v)} data-testid="territory-active" />
            <span className="text-sm text-slate-700">Active</span></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="territory-save" className="bg-[#006090] hover:bg-[#004E75]">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
